import React, { useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../utils/backendUrl';
import * as authUtils from '../utils/auth';

import { FAQ_SECTIONS } from '../utils/faqData';

const CATEGORIES = [
  'Planning workflow',
  'Billing or credits',
  'Catalog or shopping',
  'Account or login',
  'Bug report',
  'Feature request',
  'Other'
];

const HelpCenterPage = () => {
  const { user } = useAuth();
  const captcha = useMemo(() => {
    const a = 3 + Math.floor(Math.random() * 6);
    const b = 2 + Math.floor(Math.random() * 5);
    return { question: `${a} + ${b}`, answer: String(a + b) };
  }, []);

  const [form, setForm] = useState({
    category: CATEGORIES[0],
    email: user?.email || '',
    message: '',
    captchaAnswer: '',
    website: ''
  });
  const [status, setStatus] = useState({ type: '', text: '' });
  const [sending, setSending] = useState(false);

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submitSupport = async (event) => {
    event.preventDefault();
    setStatus({ type: '', text: '' });
    if (form.website) return;
    if (form.captchaAnswer.trim() !== captcha.answer) {
      setStatus({ type: 'error', text: 'Please answer the quick anti-spam check correctly.' });
      return;
    }
    if (form.message.trim().length < 12) {
      setStatus({ type: 'error', text: 'Please add a little more detail so support can help.' });
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authUtils.getAuthHeader()
        },
        body: JSON.stringify({
          category: form.category,
          email: form.email,
          message: form.message,
          honeypot: form.website
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Could not send your message.');
      setStatus({ type: 'success', text: 'Message sent. We will reply from support@craftmyparty.com.' });
      setForm((prev) => ({ ...prev, message: '', captchaAnswer: '' }));
    } catch (error) {
      setStatus({ type: 'error', text: error.message || 'Could not send your message.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8">
        <p className="text-sm font-bold uppercase tracking-[0.18em] text-purple-600">Help Center</p>
        <h1 className="mt-2 text-3xl font-extrabold text-gray-950">Find answers or contact support</h1>
        <p className="mt-2 max-w-2xl text-gray-600">
          Quick guidance for planning decisions, credits, billing, catalog picks, and account questions.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-5">
          {FAQ_SECTIONS.map((section) => (
            <section key={section.title} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-extrabold text-gray-950">{section.title}</h2>
              <div className="mt-4 space-y-3">
                {section.items.map((faq) => (
                  <details key={faq.q} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                    <summary className="cursor-pointer text-sm font-bold text-gray-950">{faq.q}</summary>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600">{faq.a}</p>
                  </details>
                ))}
              </div>
            </section>
          ))}
        </div>

        <aside className="h-fit rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
          <h2 className="text-xl font-extrabold text-gray-950">Contact support</h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            Send a message without leaving the app. Categories help us route it faster.
          </p>
          <form className="mt-5 space-y-4" onSubmit={submitSupport}>
            <input
              type="text"
              value={form.website}
              onChange={(e) => updateField('website', e.target.value)}
              className="hidden"
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
            />
            <label className="block">
              <span className="text-sm font-bold text-gray-800">Category</span>
              <select
                value={form.category}
                onChange={(e) => updateField('category', e.target.value)}
                className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm font-semibold text-gray-900 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100"
              >
                {CATEGORIES.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-bold text-gray-800">Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                required
                className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-3 text-sm font-semibold text-gray-900 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100"
              />
            </label>
            <label className="block">
              <span className="text-sm font-bold text-gray-800">Message</span>
              <textarea
                value={form.message}
                onChange={(e) => updateField('message', e.target.value)}
                rows={6}
                required
                placeholder="Tell us what happened, what you expected, and which plan or screen you were using."
                className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-3 text-sm text-gray-900 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100"
              />
            </label>
            <label className="block">
              <span className="text-sm font-bold text-gray-800">Anti-spam check: {captcha.question}</span>
              <input
                value={form.captchaAnswer}
                onChange={(e) => updateField('captchaAnswer', e.target.value)}
                required
                className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-3 text-sm font-semibold text-gray-900 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100"
              />
            </label>
            {status.text && (
              <p className={`rounded-xl px-3 py-2 text-sm font-semibold ${status.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                {status.text}
              </p>
            )}
            <button
              type="submit"
              disabled={sending}
              className="h-11 w-full rounded-xl bg-primary px-4 text-sm font-bold text-white shadow-sm transition hover:bg-primary-dark disabled:cursor-wait disabled:opacity-70"
            >
              {sending ? 'Sending...' : 'Send message'}
            </button>
            <p className="text-xs leading-relaxed text-gray-500">
              Prefer email? Write to support@craftmyparty.com.
            </p>
          </form>
        </aside>
      </div>
    </div>
  );
};

export default HelpCenterPage;
